import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as legalEntity_annotationServiceGetGetAccessGroupAnnotation } from '@festina/legalentity-general-types/legalentity/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import type { BusinessType } from '@festina/life-general-types/life'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as life_annotationServiceGetGetAccessGroupAnnotation } from '@festina/life-general-types/life/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as moneyIn_annotationServiceGetGetAccessGroupAnnotation } from '@festina/moneyin-general-types/moneyin/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as moneyOut_annotationServiceGetGetAccessGroupAnnotation } from '@festina/moneyout-general-types/moneyout/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as unitLink_annotationServiceGetGetAccessGroupAnnotation } from '@festina/unitlink-general-types/unitlink/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { useIsUserAllowedToAccessRoles } from '@festina-life/authentication'
import { notEmpty } from '@festina-life/core'

import { useHook, useService } from '../../lenteHooks'
import { useAvailableComponents } from '../useAvailableComponents'
import { getAnnotationData } from './utils'

type Props = {
  overwriteInternalId?: string
  businessType: BusinessType
}

export const useLazyAnnotations = () => {
  const { isUsingLegalEntity: isUsingLegalEntityComponent } =
    useAvailableComponents()
  const isAnnotationEnabled = useService(
    'featureFlags.annotation.isAnnotationEnabled',
  )

  // For now we'll only do a single service. When we have a query service we will change this code...
  const isUsingLife = false
  const isUsingLegalEntity = isUsingLegalEntityComponent && isAnnotationEnabled
  const isUsingMoneyIn = false
  const isUsingMoneyOut = false
  const isUsingUnitLink = false

  const { legalEntityId: routeOwnerId, tenantId } = useHook(
    'routing.general.useLegalEntityParams',
  )({
    isOptional: true,
  })
  const canAccessRoles = useIsUserAllowedToAccessRoles()
  const sources = [
    {
      component: 'legalEntity',
      enabled: isUsingLegalEntity,
      roles: legalEntity_annotationServiceGetGetAccessGroupAnnotation.roles,
      ...legalEntity_annotationServiceGetGetAccessGroupAnnotation.useLazyQuery(),
    },
    {
      component: 'life',
      enabled: isUsingLife,
      roles: life_annotationServiceGetGetAccessGroupAnnotation.roles,
      ...life_annotationServiceGetGetAccessGroupAnnotation.useLazyQuery(),
    },
    {
      component: 'moneyIn',
      enabled: isUsingMoneyIn,
      roles: moneyIn_annotationServiceGetGetAccessGroupAnnotation.roles,
      ...moneyIn_annotationServiceGetGetAccessGroupAnnotation.useLazyQuery(),
    },
    {
      component: 'moneyOut',
      enabled: isUsingMoneyOut,
      roles: moneyOut_annotationServiceGetGetAccessGroupAnnotation.roles,
      ...moneyOut_annotationServiceGetGetAccessGroupAnnotation.useLazyQuery(),
    },
    {
      component: 'unitLink',
      enabled: isUsingUnitLink,
      roles: unitLink_annotationServiceGetGetAccessGroupAnnotation.roles,
      ...unitLink_annotationServiceGetGetAccessGroupAnnotation.useLazyQuery(),
    },
  ] as const

  return async ({ overwriteInternalId, businessType }: Props) => {
    const ownerId = overwriteInternalId ?? routeOwnerId
    const annotations = await Promise.all(
      sources.map(async ({ component, enabled, roles, mutateAsync }) => {
        if (
          !enabled ||
          !tenantId ||
          !ownerId ||
          !canAccessRoles({ component, rolesToCheck: roles })
        ) {
          return { component, data: [] }
        }

        const data = await mutateAsync({
          tenantId,
          secondaryId: ownerId,
          businessType,
        })
        return { component, data: data?.groups?.filter(notEmpty) ?? [] }
      }),
    )

    return {
      legalentityAnnotations: getAnnotationData(annotations, 'legalEntity'),
      lifeAnnotations: getAnnotationData(annotations, 'life'),
      moneyInAnnotations: getAnnotationData(annotations, 'moneyIn'),
      moneyOutAnnotations: getAnnotationData(annotations, 'moneyOut'),
      unitLinkAnnotations: getAnnotationData(annotations, 'unitLink'),
    }
  }
}
